import { useState } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProperty } from "@/contexts/PropertyContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Building2, DollarSign, LogOut, ScrollText, Shield, Lock, BookOpen, Menu } from "lucide-react";

/**
 * Minimal shell for role=board users. They see exactly one thing — the Finance
 * dashboard — plus links to Terms/Privacy and their profile. No operations
 * pages, no residents, no maintenance. This keeps directors focused on the
 * numbers and shields them from making accidental changes.
 */

const BOARD_NAV = [
  { to: "/board/finance", label: "Financial dashboard", icon: DollarSign, testid: "board-nav-finance", matches: (p) => p.startsWith("/board/finance") },
  { to: "/board/vault", label: "Board vault", icon: Lock, testid: "board-nav-vault", matches: (p) => p.startsWith("/board/vault") },
  { to: "/board/wiki", label: "Board wiki", icon: BookOpen, testid: "board-nav-wiki", matches: (p) => p.startsWith("/board/wiki") },
];

export default function BoardShell() {
  const { user, logout } = useAuth();
  const { properties } = useProperty();
  const navigate = useNavigate();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const closeNav = () => setNavOpen(false);

  const communityCount = user?.property_ids?.length || properties.length;

  const desktopPill = (item) => {
    const active = item.matches(location.pathname);
    return (
      <Link
        key={item.to}
        to={item.to}
        className={
          "inline-flex items-center gap-2 text-sm rounded-full px-4 py-1.5 border transition-colors " +
          (active
            ? "bg-white text-black border-white"
            : "border-white/10 text-zinc-300 hover:text-white hover:bg-white/[0.04]")
        }
        data-testid={item.testid}
      >
        <item.icon className="h-3.5 w-3.5" /> {item.label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid-lines-dark">
      <header className="border-b border-white/10 bg-[#0a0a0b]/80 backdrop-blur sticky top-0 z-40" data-testid="board-header">
        <div className="max-w-7xl mx-auto flex items-center gap-2 sm:gap-4 px-4 sm:px-6 py-3">
          {/* Mobile menu trigger — hidden md+ where the inline pills take over. */}
          <button
            onClick={() => setNavOpen(true)}
            className="md:hidden -ml-1 p-2 rounded-md hover:bg-white/5 shrink-0"
            aria-label="Open navigation"
            data-testid="board-mobile-nav-open"
          >
            <Menu className="h-5 w-5 text-zinc-300" />
          </button>

          <Link to="/board/finance" className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-md bg-gradient-to-br from-indigo-500 to-violet-700 flex items-center justify-center shrink-0">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold font-display leading-tight truncate">Prestigious</div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 truncate">Board Portal</div>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-2 ml-6">
            {BOARD_NAV.map(desktopPill)}
          </div>

          <div className="ml-auto flex items-center gap-2 sm:gap-3 shrink-0">
            <Badge variant="outline" className="border-indigo-500/40 text-indigo-200 bg-indigo-500/10 hidden sm:inline-flex" data-testid="board-role-badge">
              <Shield className="h-3 w-3 mr-1" /> {user?.role_label || "Director"}
            </Badge>
            <div className="text-[11px] text-zinc-500 hidden lg:block">
              {communityCount} {communityCount === 1 ? "community" : "communities"}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-white/[0.04] rounded-full p-1 sm:pr-3" data-testid="board-user-menu">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-indigo-500 text-white text-xs">
                      {(user?.name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden sm:block text-left">
                    <div className="text-xs font-medium leading-tight">{user?.name}</div>
                    <div className="text-[10px] text-zinc-500">{user?.email}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#0a0a0b] border-white/10 text-white">
                <DropdownMenuLabel className="text-zinc-400 text-[11px] uppercase tracking-widest">Board</DropdownMenuLabel>
                <DropdownMenuItem className="focus:bg-white/[0.06]" asChild>
                  <Link to="/terms" data-testid="board-menu-terms"><ScrollText className="h-3.5 w-3.5 mr-2" /> Terms</Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="focus:bg-white/[0.06]" asChild>
                  <Link to="/privacy" data-testid="board-menu-privacy"><ScrollText className="h-3.5 w-3.5 mr-2" /> Privacy</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem className="focus:bg-white/[0.06] text-red-300" onClick={async () => { await logout(); navigate("/login"); }} data-testid="board-menu-logout">
                  <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <Outlet />
      </main>

      {/* Mobile navigation Sheet — mirrors the three top-nav pills. */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-72 max-w-[85vw] p-0 flex flex-col bg-[#0a0a0b] border-white/10 text-white" data-testid="board-mobile-nav-sheet">
          <SheetHeader className="p-4 border-b border-white/10 shrink-0">
            <SheetTitle className="font-display">Board Portal</SheetTitle>
            <SheetDescription className="text-zinc-500 text-xs">Financial dashboard, board vault, and wiki.</SheetDescription>
          </SheetHeader>
          <nav className="flex flex-col gap-1 p-3">
            {BOARD_NAV.map((item) => {
              const active = item.matches(location.pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeNav}
                  data-testid={`board-mobile-nav-${item.testid.replace("board-nav-", "")}`}
                  className={
                    "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors " +
                    (active
                      ? "bg-white text-black"
                      : "text-zinc-300 hover:bg-white/[0.06] hover:text-white")
                  }
                >
                  <item.icon className={"h-4 w-4 " + (active ? "text-black" : "text-zinc-500")} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );
}
